import { useState } from "react";
import { NavLink } from "react-router-dom";
import { ROUTES } from "@/app/routes";
import { IconRenderer } from "@/components/icons/IconRenderer";
import { SafeImage } from "@/components/ui/SafeImage";
import { usePublicSocialLinks } from "@/features/social-links/hooks";
import { useSiteSettings } from "@/features/site-settings/hooks";
import { cn } from "@/lib/cn";

const publicLinks = [
  { label: "About", to: ROUTES.about },
  { label: "Commissions", to: ROUTES.commissions },
  { label: "Portfolio", to: ROUTES.portfolio },
  { label: "Partners", to: ROUTES.partners },
  { label: "References", to: ROUTES.references },
  { label: "Contact", to: ROUTES.contact },
  { label: "Socials", to: ROUTES.socials },
];

const fallbackLogoUrl = "/KL-logo-long.png";

export function Navbar() {
  const siteSettings = useSiteSettings();
  const socials = usePublicSocialLinks();
  const [menuOpen, setMenuOpen] = useState(false);
  const siteName = siteSettings.data?.siteName ?? "Kalaco";
  const logoUrl = siteSettings.data?.logoUrl || fallbackLogoUrl;

  return (
    <header className="sticky top-0 z-30 border-b border-cyan-300/15 bg-ink/80 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-3 py-3 sm:px-6 lg:px-8 xl:gap-4 xl:py-4">
        <NavLink to={ROUTES.home} className="flex min-w-0 items-center gap-3" onClick={() => setMenuOpen(false)}>
          <SafeImage
            src={logoUrl}
            alt={`${siteName} logo`}
            fallbackLabel={siteName.slice(0, 2)}
            className="h-10 max-w-[155px] rounded-md object-contain sm:h-12 sm:max-w-[220px] lg:h-14 lg:max-w-[260px] xl:h-16 xl:max-w-[300px]"
          />
          {/*<span className="font-display text-lg font-bold uppercase tracking-[0.22em] text-white">{siteName}</span>*/}
        </NavLink>
        <div className="hidden items-center gap-1 xl:flex">
          {publicLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  "rounded-md px-3 py-2 text-sm font-medium transition",
                  isActive ? "bg-white/10 text-white" : "text-content-secondary hover:bg-white/5 hover:text-white",
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="hidden gap-2 xl:flex">
            {(socials.data ?? []).slice(0, 3).map((link) => (
              <a key={link.id} href={link.url} target="_blank" rel="noreferrer" className="grid size-8 place-items-center rounded border border-cyan-300/20 text-xs text-content-secondary hover:text-white" aria-label={link.label || link.platform}>
                <IconRenderer value={link.iconName} iconSet="social" />
              </a>
            ))}
          </div>
          <button
            type="button"
            className="inline-flex h-9 w-10 items-center justify-center rounded border border-cyan-300/25 bg-black/35 text-cyan-100 transition hover:border-cyan-300/60 hover:text-white xl:hidden"
            aria-controls="public-navigation"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            onClick={() => setMenuOpen((current) => !current)}
          >
            <span className="sr-only">{menuOpen ? "Close menu" : "Open menu"}</span>
            <span className="flex flex-col gap-1.5" aria-hidden="true">
              <span className={cn("block h-px w-5 bg-current transition", menuOpen && "translate-y-2 rotate-45")} />
              <span className={cn("block h-px w-5 bg-current transition", menuOpen && "opacity-0")} />
              <span className={cn("block h-px w-5 bg-current transition", menuOpen && "-translate-y-2 -rotate-45")} />
            </span>
          </button>
        </div>
      </nav>
      <div
        id="public-navigation"
        className={cn(
          "mx-auto max-w-7xl px-3 pb-3 sm:px-6 lg:px-8 xl:hidden",
          menuOpen ? "block" : "hidden",
        )}
      >
        <div className="grid gap-2 rounded-md border border-cyan-300/15 bg-black/45 p-2 sm:grid-cols-2 md:grid-cols-3">
        {publicLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              cn(
                "min-w-0 rounded-md border px-3 py-2 text-sm transition",
                isActive ? "border-cyan-300/60 bg-cyan-300/10 text-white" : "border-white/10 text-content-secondary hover:border-cyan-300/40 hover:text-white",
              )
            }
          >
            {link.label}
          </NavLink>
        ))}
        </div>
      </div>
    </header>
  );
}
